import React, { useState, useEffect } from 'react'
import './styles/CookieBanner.css'
import {
  geoLocationService,
  quickSetup,
  generateCookiePolicyText,
  COOKIE_CATEGORIES,
  REGIONS
} from '../../utils/cookiesPolicy/index.js'
import CookieSettings from './CookieSettings'
import { FaArrowRight } from 'react-icons/fa'

const CookieBanner = ({
  onAcceptAll,
  onRejectAll,
  onCustomize,
  onClose,
  isVisible = true
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [locationInfo, setLocationInfo] = useState(null)
  const [policyText, setPolicyText] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [preferences, setPreferences] = useState({})
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    initializeBanner()
  }, [])

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true)
      // Trigger entrance animation
      setTimeout(() => setIsAnimating(false), 100)
    }
  }, [isVisible])

  const initializeBanner = async () => {
    try {
      const info = await geoLocationService.getDetailedLocationInfo()
      const text = generateCookiePolicyText(info.region)

      setLocationInfo(info)
      setPolicyText(text)

      // Set initial preferences based on country
      const initialPrefs = getInitialPreferences(info.region)
      setPreferences(initialPrefs)

      setIsLoading(false)
    } catch (error) {
      console.error('Error initializing banner:', error)
      // Use fallback data
      const fallbackInfo = {
        region: 'eu',
        complianceRules: { requiresExplicitConsent: true, showRejectAll: true }
      }
      setLocationInfo(fallbackInfo)
      setPolicyText(generateCookiePolicyText('eu'))
      setPreferences(getInitialPreferences('eu'))
      setIsLoading(false)
    }
  }

  const getInitialPreferences = (region) => {
    if (region === REGIONS.US_CALIFORNIA || region === REGIONS.US_OTHER) {
      return {
        [COOKIE_CATEGORIES.ESSENTIAL]: true,
        [COOKIE_CATEGORIES.PERFORMANCE]: true,
        [COOKIE_CATEGORIES.FUNCTIONAL]: true,
        [COOKIE_CATEGORIES.MARKETING]: true
      }
    }

    // For Canada: only required cookies pre-checked
    if (region === 'ca') {
      return {
        [COOKIE_CATEGORIES.ESSENTIAL]: true,
        [COOKIE_CATEGORIES.PERFORMANCE]: false,
        [COOKIE_CATEGORIES.FUNCTIONAL]: false,
        [COOKIE_CATEGORIES.MARKETING]: false
      }
    }

    // For other regions (EU, UK, etc.): only essential
    return {
      [COOKIE_CATEGORIES.ESSENTIAL]: true,
      [COOKIE_CATEGORIES.PERFORMANCE]: false,
      [COOKIE_CATEGORIES.FUNCTIONAL]: false,
      [COOKIE_CATEGORIES.MARKETING]: false
    }
  }

  const handleAcceptAll = async () => {
    try {
      setIsLoading(true)
      // Save current preferences (based on what user has selected)
      const success = await quickSetup.savePreferences(
        preferences,
        locationInfo?.region
      )

      if (success && onAcceptAll) {
        onAcceptAll()
      }

      handleClose()
    } catch (error) {
      console.error('Error accepting cookies:', error)
      setIsLoading(false)
    }
  }

  const handleRejectAll = async () => {
    try {
      setIsLoading(true)
      const success = await quickSetup.rejectAll(locationInfo?.region)

      if (success && onRejectAll) {
        onRejectAll()
      }

      handleClose()
    } catch (error) {
      console.error('Error rejecting cookies:', error)
      setIsLoading(false)
    }
  }

  const handleCustomize = () => {
    setShowSettings(true)
  }

  const handleSettingsClose = () => {
    setShowSettings(false)
  }

  const handleSettingsSave = (newPreferences) => {
    setPreferences(newPreferences)
    setShowSettings(false)
    handleClose()
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      if (onClose) {
        onClose()
      }
    }, 300) // Match animation duration
  }

  const getComplianceMessage = () => {
    if (!locationInfo) return ''

    const { region, complianceRules } = locationInfo

    if (
      complianceRules.regulation === 'GDPR' ||
      complianceRules.regulation === 'UK GDPR'
    ) {
      return 'We need your consent to use cookies on this website.'
    }

    if (complianceRules.regulation === 'CCPA/CPRA') {
      return 'We use cookies to improve your experience.'
    }

    if (complianceRules.regulation === 'PIPEDA') {
      return 'We use cookies to enhance your experience. '
    }

    return 'We use cookies to enhance your experience on our website.'
  }

  const getButtonConfiguration = () => {
    if (!locationInfo) return { showRejectAll: true }

    const { complianceRules } = locationInfo

    return {
      showRejectAll: complianceRules.showRejectAll !== false,
      acceptText: 'Accept Cookies',
      rejectText: 'Reject All'
    }
  }

  if (!isVisible || isLoading) {
    return null
  }

  const buttonConfig = getButtonConfiguration()

  return (
    <>
      <div
        className={`cookie-banner ${
          isVisible && !isLoading ? 'cookie-banner--visible' : ''
        } ${isAnimating ? 'cookie-banner--entering' : ''} ${
          isClosing ? 'cookie-banner--closing' : ''
        }`}
        role='dialog'
        aria-labelledby='cookie-banner-title'
        aria-describedby='cookie-banner-description'
      >
        {/* Background overlay for better visibility */}
        <div className='cookie-banner__overlay' />

        <div className='cookie-banner__container'>
          <div className='cookie-banner__content'>
            <div className='cookie-banner__icon'>🍪</div>

            <div className='cookie-banner__text'>
              <h3 id='cookie-banner-title' className='cookie-banner__title'>
                Quick Cookie Check
              </h3>
              <div className='cookie-banner__description-container'>
                <p
                  id='cookie-banner-description'
                  className='cookie-banner__description'
                >
                  We use cookies to give you a better experience.
                  {/* <br />
                  Accept to continue, or manage your preferences. */}
                  {/* Find out more{' '}
                <strong>
                  <span
                    onClick={handleCustomize}
                    className='cookie-banner__link_bold'
                  >
                    on how we use cookies <br /> and how you can change your
                    settings.
                  </span>
                </strong> */}
                </p>
                <div className='cookie-banner__primary-actions'>
                  <button
                    type='button'
                    className='cookie-banner__button cookie-banner__button--manage'
                    onClick={handleCustomize}
                  >
                    Manage Cookies
                  </button>
                  <button
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    type='button'
                    className='cookie-banner__button cookie-banner__button--accept'
                    onClick={handleAcceptAll}
                  >
                    Accept All & Continue{' '}
                    <FaArrowRight aria-hidden='true' focusable='false' />
                  </button>
                </div>
              </div>
              {locationInfo?.testOverride && (
                <div className='cookie-banner__test-notice'>
                  🧪 Test Mode: {locationInfo.region.toUpperCase()}
                </div>
              )}
            </div>
          </div>

          <div className='cookie-banner__actions'>
            <div className='cookie-banner__button-group'>
              {buttonConfig.showRejectAll && (
                <button
                  type='button'
                  className='cookie-banner__button cookie-banner__button--reject'
                  onClick={handleRejectAll}
                >
                  {buttonConfig.rejectText}
                </button>
              )}
            </div>

            <div className='cookie-banner__links'>
              <a
                href='/privacy-policy-en'
                target='_blank'
                className='cookie-banner__link'
                rel='noopener noreferrer'
              >
                Privacy Policy
              </a>
              <span className='cookie-banner__separator'>•</span>
              <a
                href='/cookie-policy-en'
                target='_blank'
                className='cookie-banner__link'
                rel='noopener noreferrer'
              >
                Cookie Policy
              </a>
            </div>
          </div>

          {/* Close button for accessibility */}
          {/* <button
            type='button'
            className='cookie-banner__close'
            onClick={handleClose}
            aria-label='Close cookie banner'
          >
            ×
          </button> */}
        </div>

        {/* Compliance indicators */}
        {/* <div className='cookie-banner__compliance'>
        {locationInfo?.complianceRules?.regulation && (
          <span className='cookie-banner__compliance-badge'>
            {locationInfo.complianceRules.regulation}
          </span>
        )}
      </div> */}
      </div>

      {/* Cookie Settings Modal - Rendered outside banner for proper centering */}
      {showSettings && (
        <CookieSettings
          isOpen={showSettings}
          onClose={handleSettingsClose}
          onSave={handleSettingsSave}
          initialPreferences={preferences}
        />
      )}
    </>
  )
}

export default CookieBanner
